"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BellIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DailyForm } from "@/components/daily-form";
import { api } from "@/lib/api";
import { formatSavedAt } from "@/lib/kyiv";
import { givenName } from "@/lib/names";
import { isStaff } from "@/lib/roles";
import type { DailyMarkDto, MeResponse, TodayResponse } from "@/lib/types";
import { cn } from "@/lib/utils";
import { absentWord } from "@/lib/child-grammar";
import { StatusScreen } from "@/components/status-screen";
import { SmoothReveal } from "@/components/smooth-reveal";
import { useLiveReload } from "@/components/live-provider";

const filters = [
  { id: "all", label: "Усі" },
  { id: "silent", label: "Мовчать" },
  { id: "breakfast", label: "Сніданок" },
  { id: "group", label: "Група" },
  { id: "absent", label: "Відсутні" },
] as const;

export function TodayBoard({
  me,
  today: controlled,
  initialToday,
  active = true,
  onChanged,
}: {
  me: MeResponse;
  today?: TodayResponse;
  initialToday?: TodayResponse;
  active?: boolean;
  onChanged?: () => void;
}) {
  const [today, setToday] = useState<TodayResponse | null>(controlled ?? initialToday ?? null);
  const [filter, setFilter] = useState<(typeof filters)[number]["id"]>("all");
  const [loading, setLoading] = useState(!controlled && !initialToday);
  const [reminding, setReminding] = useState(false);
  const [markingId, setMarkingId] = useState<string | null>(null);

  async function load() {
    const data = await api<TodayResponse>("/api/today");
    setToday(data);
    setLoading(false);
  }

  useEffect(() => {
    if (controlled) {
      setToday(controlled);
      setLoading(false);
      return;
    }
    void load().catch((error) => {
      toast.error(error.message);
      setLoading(false);
    });
  }, [controlled]);

  useLiveReload("today", load, active && !controlled);

  const rows = useMemo(() => {
    if (!today) return [];
    return today.children.filter((row) => {
      if (filter === "silent") return !row.responded;
      if (filter === "breakfast") return row.breakfast === true;
      if (filter === "group") return row.afterschool === true;
      if (filter === "absent") return row.responded && row.absent;
      return true;
    });
  }, [today, filter]);

  const staffCanMark = Boolean(today?.editable && isStaff(me.user.role));
  const marking = today?.children.find((row) => row.childId === markingId) ?? null;

  async function remind() {
    setReminding(true);
    try {
      const result = await api<{ silentCount: number; wouldNotify: number; demo?: boolean }>(
        "/api/remind",
        { method: "POST" },
      );
      if (result.demo) {
        toast.success(
          `У демо бот не надсилає Telegram. Без відповіді: ${result.silentCount}. У житті лист прийшов би ${result.wouldNotify} батькам.`,
        );
      } else {
        toast.success(`Нагадування надіслано. Без відповіді: ${result.silentCount}.`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося нагадати.");
    } finally {
      setReminding(false);
    }
  }

  if (loading || !today) {
    return <StatusScreen loading title="Дивимось, хто як" text="Хто снідає, хто на групі, хто ще не відповів." />;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 wide:grid-cols-4">
        <Stat
          label="Сніданок"
          value={today.totals.breakfast}
          hint={today.totals.present ? `з ${today.totals.present} у школі` : "ніхто ще не в школі"}
        />
        <Stat
          label="Група"
          value={today.totals.afterschool}
          hint={today.totals.present ? `з ${today.totals.present} у школі` : "ніхто ще не в школі"}
        />
        <Stat label="Відсутні" value={today.totals.absent} hint="цього ранку не будуть" />
        <Stat label="Мовчать" value={today.totals.silent} hint="ще немає відмітки" warn={today.totals.silent > 0} />
      </div>

      {today.totals.silent > 0 && isStaff(me.user.role) && today.date === today.calendarDate ? (
        <Button
          variant="outline"
          className="h-11 w-full rounded-2xl"
          onClick={() => void remind()}
          disabled={reminding}
        >
          <BellIcon className="size-4" />
          {reminding ? "Надсилаємо…" : "Нагадати тим, хто мовчить"}
        </Button>
      ) : today.totals.silent > 0 ? null : (
        <p className="rounded-2xl bg-card px-4 py-3 text-sm ring-1 ring-foreground/8">
          {today.totals.absent > 0
            ? `Усі відмітились. Відсутні: ${today.totals.absent}. Сніданок і група рахуються лише для тих, хто в школі.`
            : "Усі відмітились. Можна звіряти сніданки й групу без підрахунку в чаті."}
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {filters.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setFilter(item.id)}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap",
              filter === item.id ? "bg-primary text-primary-foreground" : "bg-accent text-foreground",
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {staffCanMark ? (
        <p className="text-sm leading-5 text-muted-foreground">
          Якщо батьки подзвонили — «Відмітити» біля дитини. Окреме повідомлення їм не йде.
        </p>
      ) : null}

      <ul className="wide-cards">
        {rows.map((row) => (
          <li key={row.childId} className="rounded-2xl bg-card px-4 py-3 ring-1 ring-foreground/8">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{row.childName}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {!row.responded
                    ? "Ще немає відповіді"
                    : row.absent
                      ? absentWord(row.gender, true)
                      : `${row.breakfast ? "Сніданок" : "Без сніданку"} · ${row.afterschool ? "група" : "додому"}`}
                </p>
                {row.note ? <p className="mt-1 text-sm leading-5">{row.note}</p> : null}
                <MarkHistory row={row} />
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px] font-medium",
                    !row.responded
                      ? "bg-destructive/10 text-destructive"
                      : row.absent
                        ? "bg-accent text-muted-foreground"
                        : "bg-primary/10 text-primary",
                  )}
                >
                  {!row.responded ? "чекаємо" : row.absent ? "немає" : "є"}
                </span>
                {staffCanMark ? (
                  <button
                    type="button"
                    className="text-sm font-medium text-primary"
                    onClick={() => setMarkingId(row.childId)}
                  >
                    {row.responded ? "Змінити" : "Відмітити"}
                  </button>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {rows.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground">У цьому фільтрі нікого немає.</p>
      ) : null}

      <Dialog open={Boolean(marking)} onOpenChange={(open) => !open && setMarkingId(null)}>
        <DialogContent className="max-h-[min(90dvh,720px)] overflow-y-auto rounded-3xl p-4 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{marking ? givenName(marking.childName) : "Відмітка"}</DialogTitle>
            <DialogDescription>
              {marking
                ? `${marking.childName} · ${today.label}. Збережеться як відмітка класного керівника.`
                : "Відмітка замість батьків."}
            </DialogDescription>
          </DialogHeader>
          {marking ? (
            <DailyForm
              key={`${marking.childId}:${marking.updatedAt ?? "new"}`}
              me={me}
              today={today}
              assist
              forChildren={[{ id: marking.childId, name: marking.childName, gender: marking.gender }]}
              onSaved={() => {
                setMarkingId(null);
                if (onChanged) onChanged();
                else if (!controlled) void load();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MarkHistory({ row }: { row: DailyMarkDto }) {
  const [open, setOpen] = useState(false);
  if (row.events.length < 2) return null;
  return (
    <div className="mt-2">
      <button
        type="button"
        className="cursor-pointer text-xs font-medium text-primary"
        onClick={() => setOpen((next) => !next)}
        aria-expanded={open}
      >
        Зміни · {row.events.length}
      </button>
      <SmoothReveal open={open}>
        <ul className="mt-2 space-y-1.5 text-xs leading-5 text-muted-foreground">
          {row.events.map((event) => (
            <li key={event.id}>
              {formatSavedAt(event.at)} · {event.authorName} · {event.summary}
            </li>
          ))}
        </ul>
      </SmoothReveal>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  warn,
}: {
  label: string;
  value: number;
  hint: string;
  warn?: boolean;
}) {
  return (
    <div className="rounded-3xl bg-card px-3 py-4 text-center ring-1 ring-foreground/8">
      <p className={cn("font-heading text-2xl font-semibold", warn && "text-destructive")}>{value}</p>
      <p className="mt-1 text-xs font-medium">{label}</p>
      <p className="text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}
